import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ConfirmModal } from './Modal';

/**
 * App-wide feedback: in-app confirm dialogs (replacing window.confirm) and
 * transient toasts for "it worked" messages after an action.
 */

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ToastKind = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
}

interface FeedbackContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  toast: (text: string, kind?: ToastKind) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const confirm = useCallback((opts: ConfirmOptions) => {
    resolverRef.current?.(false);
    setPending(opts);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = (ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setPending(null);
  };

  const toast = useCallback((text: string, kind: ToastKind = 'success') => {
    const id = nextId.current++;
    setToasts((list) => [...list, { id, kind, text }]);
    window.setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), kind === 'error' ? 7000 : 4000);
  }, []);

  return (
    <FeedbackContext.Provider value={{ confirm, toast }}>
      {children}
      <ConfirmModal
        open={pending !== null}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        title={pending?.title ?? ''}
        message={pending?.message ?? ''}
        confirmLabel={pending?.confirmLabel}
        cancelLabel={pending?.cancelLabel}
        danger={pending?.danger}
      />
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            <span>{t.text}</span>
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss"
              onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <style>{`
        .toast-stack {
          position: fixed;
          left: 50%;
          bottom: var(--space-lg);
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
          z-index: 1100;
          width: min(480px, calc(100vw - 2 * var(--space-md)));
          pointer-events: none;
        }
        .toast {
          pointer-events: auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-md);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-md);
          background: var(--color-ink);
          color: var(--color-white);
          font-size: 0.9375rem;
          border-left: 4px solid var(--color-steel);
        }
        .toast-success { border-left-color: var(--color-ok); }
        .toast-error { border-left-color: var(--color-urgent); }
        .toast-info { border-left-color: var(--color-signal); }
        .toast-close {
          background: none;
          border: none;
          color: inherit;
          font-size: 1.25rem;
          line-height: 1;
          cursor: pointer;
          opacity: 0.7;
          padding: var(--space-xs);
        }
      `}</style>
    </FeedbackContext.Provider>
  );
}

function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useConfirm/useToast must be used within FeedbackProvider');
  return ctx;
}

/** `if (!(await confirm({ title, message }))) return;` — in-app replacement for window.confirm. */
export function useConfirm() {
  return useFeedback().confirm;
}

/** `toast('Invoice finalised')` or `toast(err.message, 'error')`. */
export function useToast() {
  return useFeedback().toast;
}
